"use client"

import { ColumnDef, RowData } from "@tanstack/react-table"
import { Check, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"
import { useContext } from "react"
import { AppContext } from "@/App"
import { AcknowledgedIcon, SeenIcon } from "@/Components/ui/Icons/TooltipIcon"
import { PageLockContext } from "@/Components/PageLockProvider"
import { DateCell } from "@/Components/datatable/cells/date_cell"
import { WorkPackageCell } from "@/Components/datatable/cells/workpackage_cell"
import { TextCell } from "@/Components/datatable/cells/text_cell"
import { OwnerCell } from "@/Components/datatable/cells/owner_cell"
import { StatusCell } from "@/Components/datatable/cells/status_cell"
import { RaisedByCell } from "@/Components/datatable/cells/raisedby_cell"
import { Decision } from "@/Components/Decisions/Decision"

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'date-range' | 'select' | 'bool-select'
    }
}

export function getDecisionColumns(HandleDeleteClicked: (ids: number[]) => void, HandleEditClicked: (id: number) => void): ColumnDef<Decision>[] {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);

    const columns: ColumnDef<Decision>[] = [];

    if (app.authLevel !== 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && "indeterminate")
                        }
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                        id="table-select-all"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        onClick={(e) => e.stopPropagation()}
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                        className="table-row-checkbox"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            }
        );
    }

    columns.push(
        {
            id: "decisionId",
            accessorKey: "decisionId",
            header: "ID",
            cell: ({ row }) => {
                const decisionId = row.getValue("decisionId");
                return <div className="text-center">{decisionId as number}</div>
            },
            size: 50,
        },
        {
            id: "date",
            accessorKey: "date",
            header: "Date Raised",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DateCell,
            filterFn: (row, columnId, filterValue) => {
                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
            size: 120,
        });

    if (app.selectedProject?.projectTypeId === 2) {

        columns.push({
            id: "workstream",
            accessorKey: "workstream",
            header: "WS",
            meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const workstream = row.getValue("workstream");
                return <div className="text-center">{workstream as string}</div>
            },
            size: 120,
        });
    }
    columns.push(
        {
            id: "workPackage",
            accessorKey: "workPackage",
            header: "WP",
            meta: {
                filterVariant: 'select',
            },
            cell: WorkPackageCell,
            size: 120,
        },
        // {
        //     id: "topic",
        //     accessorKey: "topic",
        //     header: "Topic",
        //     cell: TextCell,
        //     size: 150,
        // },
        {
            id: "description",
            accessorKey: "description",
            header: "Decision",
            cell: TextCell,
            size: 400,
        },
        {
            id: "raisedBy",
            accessorKey: "raisedBy",
            header: "Raised By",
            cell: RaisedByCell,
            size: 110,
        },
        {
            id: "decider",
            accessorKey: "decider",
            header: "Decision Maker",
            cell: OwnerCell,
            size: 110,
        },
        {
            id: "dateMade",
            accessorKey: "dateMade",
            header: "Decision Date",
            meta: {
                filterVariant: 'date-range',
            },
            cell: DateCell,
            filterFn: (row, columnId, filterValue) => {
                const value = new Date(row.getValue(columnId));
                const from = filterValue.from ? new Date(filterValue.from) : null;
                const to = filterValue.to ? new Date(filterValue.to) : null;
                if (!from || !to) return true;
                return value >= from && value <= to;
            },
            size: 120,
        },
        // {
        //     id: "meeting",
        //     accessorKey: "meeting",
        //     header: "Meeting",
        //     cell: TextCell,
        // },
        {
            id: "status",
            accessorKey: "status",
            header: "Status",
            meta: {
                filterVariant: 'select',
            },
            cell: StatusCell,
        },
        {
            id: "notes",
            accessorKey: "notes",
            header: "Notes",
            cell: TextCell,
        },
        {
            id: "seen",
            accessorKey: "seen",
            header: () => {
                return (<SeenIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const seen = row.getValue("seen");
                return <>{seen ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        },
        {
            id: "acknowledged",
            accessorKey: "acknowledged",
            header: () => {
                return (<AcknowledgedIcon />)
            },
            meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const acknowledged = row.getValue("acknowledged");
                return <>{acknowledged ? <Check className="m-auto" /> : ""}</>
            },
            filterFn: (row, columnId, filterValue) => {
                // Custom filtering logic for the "accepted" column
                if (filterValue === "true") {
                    return row.getValue(columnId) === true; // Show only true values
                } else if (filterValue === "false") {
                    return row.getValue(columnId) === false; // Show only false values
                } else {
                    return true; // Show all values if filterValue is not specified (e.g., reset/all)
                }
            },
            size: 80,
        }
    );

    if (app.authLevel !== 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "actions",
                cell: ({ row }) => {
                    const Decision = row.original

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 table-row-menu-button">
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                {!app.applicationUser?.userSettings?.inlineTableEditing && (
                                    <DropdownMenuItem
                                        onClick={(e) => { e.stopPropagation(); HandleEditClicked(Decision.decisionId); }}
                                    >
                                        Edit
                                    </DropdownMenuItem>
                                )}
                                {app.authLevel > 3 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                            onClick={(e) => { e.stopPropagation(); HandleDeleteClicked([Decision.decisionId]) }}
                                        >
                                            Delete
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )
                },
            }
        );
    }

    return columns;
}

export const columnDisplayNames: [string, string][] = [
    ["decisionId", "ID"],
    ["date", "Date Raised"],
    ["workstream", "Workstream"],
    ["workPackage", "Work Package"],
    ["description", "Decision"],
    ["raisedBy", "Raised By"],
    ["decider", "Decision Maker"],
    ["dateMade", "Decision Date"],
    ["status", "Status"],
    ["notes", "Notes"],
    ["seen", "Seen"],
    ["acknowledged", "Acknowledged"],
]